import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import {
	ArrowRightStartOnRectangleIcon,
	CalendarDaysIcon,
	// ChatBubbleLeftRightIcon, // DEPRECATED: Chat with Provider menu hidden
	ClipboardDocumentCheckIcon,
	ClockIcon,
	Cog6ToothIcon,
	// CreditCardIcon, // Temporarily removed - pricing menu hidden
	FolderIcon,
	GlobeAltIcon,
	ShieldCheckIcon,
	UserIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserData } from "../contexts/user-data-context";
import { useAnalytics } from "../lib/analytics";
import { performLogout } from "../lib/auth-logout";
import { useClientTranslations } from "../lib/i18n/client";
import { isValidAvatarUrl } from "../lib/utils/avatarUrl";
import { LanguageSelector } from "./i18n/LanguageSelector";
import { useGlobalLoading } from "./loading/global-loading-context";
import { MenuFooter } from "./MenuFooter";
import styles from "./SideMenu.module.css";

/** Surface the menu is shown on — drives the `context` analytics property. */
type MenuContext = "app" | "onboarding" | "twofactor";

interface SideMenuProps {
	isOpen: boolean;
	onClose: () => void;
	/** When true, only shows profile pic and language selector (for 2FA/onboarding pages) */
	minimal?: boolean;
	/** Where the menu is shown; tags analytics so onboarding/2FA/app can be told apart. */
	context?: MenuContext;
}

// auth_logout trigger per surface (keeps the signout event in the auth funnel
// while still attributing it to the right menu).
const LOGOUT_TRIGGER: Record<MenuContext, string> = {
	app: "side_menu",
	onboarding: "onboarding_menu",
	twofactor: "twofactor_menu",
};

// Top-nav destinations (UpperNav lives on the homepage only, so these surface
// them on every other page). Driven from one array so the three buttons share
// a single markup + active/disabled rule and can't drift apart.
const TOP_NAV_DESTINATIONS = [
	{
		path: "/files",
		Icon: FolderIcon,
		labelKey: "home.nav.files",
		event: "menu_files_click",
	},
	{
		path: "/get-ready",
		Icon: ClipboardDocumentCheckIcon,
		labelKey: "home.nav.prepare",
		event: "menu_get_ready_click",
	},
	{
		path: "/track",
		Icon: CalendarDaysIcon,
		labelKey: "home.nav.track",
		event: "menu_track_click",
	},
] as const;

export function SideMenu({
	isOpen,
	onClose,
	minimal = false,
	context = "app",
}: SideMenuProps) {
	const { t } = useClientTranslations("common");
	const navigate = useNavigate();
	const location = useLocation();
	const { setLoading } = useGlobalLoading();
	const analytics = useAnalytics();
	const { user, logout } = useAuth0();
	const { userData } = useUserData();

	// Check role from Auth0 custom claims
	const userRole = user?.["https://eraborn.com/role"] || "member";
	const isAdmin = Boolean(userRole === "admin");

	// Use either user data or Auth0 user data
	const displayName = userData?.displayName || user?.name || "User";
	const displayEmail = user?.email || "";

	const currentUserImage = userData?.userImage || user?.picture;
	const validUserImage = isValidAvatarUrl(currentUserImage)
		? currentUserImage
		: null;

	const handleClose = (method: "x_button" | "overlay") => {
		analytics.logShared("menu_closed", undefined, { context, method });
		onClose();
	};

	const handleSignOut = async () => {
		analytics.logAuth("logout", {
			trigger: LOGOUT_TRIGGER[context],
			platform: Capacitor.getPlatform(),
		});
		await performLogout(logout);
	};

	const handleNavigation = (href: string, eventName: string) => {
		onClose();
		analytics.logEvent(eventName);

		// Check if we're already on the target page
		if (location.pathname === href) {
			return;
		}

		// Show loading spinner
		setLoading(true);
		navigate(href);
	};

	return (
		<>
			{/* Menu Overlay */}
			{isOpen && (
				<button
					type="button"
					className={styles.menuOverlay}
					onClick={() => handleClose("overlay")}
					aria-label="Close menu"
				/>
			)}

			{/* Sliding Menu */}
			<div
				className={`${styles.menuContainer} ${isOpen ? styles.open : styles.closed}`}
			>
				<div className={styles.menuContent}>
					{/* Menu Header */}
					<div className={styles.menuHeader}>
						<h2 className={styles.menuTitle}>
							{minimal ? t("general.settings") : t("general.menu")}
						</h2>
						<button
							type="button"
							onClick={() => handleClose("x_button")}
							className={styles.menuCloseButton}
							aria-label="Close menu"
						>
							<svg
								className={styles.menuCloseIcon}
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>

					{minimal ? (
						// Minimal menu for 2FA and onboarding pages
						<div className={styles.minimalMenuContent}>
							{/* User Profile Section */}
							<div className={styles.profileSection}>
								<div className={styles.profileImageContainer}>
									{validUserImage ? (
										<img
											src={validUserImage}
											alt="Profile"
											className={styles.profileImage}
										/>
									) : (
										<img
											src="/images/era-icon-black.svg"
											alt="Era"
											className={styles.profileImage}
										/>
									)}
								</div>
								<div className={styles.profileInfo}>
									<div className={styles.profileName}>
										<p className={styles.profileNameText}>{displayName}</p>
										{isAdmin && (
											<span className={styles.adminBadge}>
												{t("navigation.admin")}
											</span>
										)}
									</div>
									<p className={styles.profileEmail}>{displayEmail}</p>
								</div>
							</div>

							{/* Language Selector */}
							<div className={styles.languageSection}>
								<div className={styles.languageLabel}>
									<GlobeAltIcon className={styles.languageIcon} />
									{t("general.displayLanguage")}
								</div>
								<LanguageSelector
									variant="compact"
									className="w-full"
									context={context}
								/>
							</div>

							{/* Sign Out Button */}
							<button
								type="button"
								onClick={handleSignOut}
								className={styles.signOutButton}
							>
								<div className={styles.signOutContent}>
									<ArrowRightStartOnRectangleIcon
										className={styles.signOutIcon}
									/>
									<span>{t("buttons.signOut")}</span>
								</div>
							</button>
						</div>
					) : (
						// Full menu for regular pages
						<>
							{/* Menu Items */}
							<nav className={styles.menuNav}>
								<button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/" ? styles.active : ""}`}
									onClick={() => handleNavigation("/", "menu_home_click")}
									disabled={location.pathname === "/"}
								>
									<svg
										className={styles.menuIcon}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
										/>
									</svg>
									<span>{t("navigation.home")}</span>
								</button>

								<button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/chat-history" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/chat-history", "menu_chat_history_click")
									}
									disabled={location.pathname === "/chat-history"}
								>
									<ClockIcon className={styles.menuIcon} />
									<span>{t("navigation.chatHistory")}</span>
								</button>

								{/* Top-nav destinations (UpperNav lives on the homepage only, so
								    these surface them on every other page). */}
								{TOP_NAV_DESTINATIONS.map(({ path, Icon, labelKey, event }) => {
									const isActive = location.pathname.startsWith(path);
									return (
										<button
											key={path}
											type="button"
											className={`${styles.menuItem} ${isActive ? styles.active : ""}`}
											onClick={() => handleNavigation(path, event)}
											disabled={isActive}
										>
											<Icon className={styles.menuIcon} />
											<span>{t(labelKey)}</span>
										</button>
									);
								})}

								{/* DEPRECATED: Page scheduled for removal in future milestone */}
								{/* <button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/chat-with-provider" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation(
											"/chat-with-provider",
											"menu_chat_with_provider_click",
										)
									}
									disabled={location.pathname === "/chat-with-provider"}
								>
									<ChatBubbleLeftRightIcon className={styles.menuIcon} />
									<span>{t("navigation.chatWithProvider")}</span>
								</button> */}

								{/* DEPRECATED: Page scheduled for removal in future milestone */}
								{/* <button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/documents" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/documents", "menu_documents_click")
									}
									disabled={location.pathname === "/documents"}
								>
									<svg
										className={styles.menuIcon}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
									<span>{t("navigation.documents")}</span>
								</button> */}

								{/* DEPRECATED: Page scheduled for removal in future milestone */}
								{/* <button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/meet-expert" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/meet-expert", "menu_meet_expert_click")
									}
									disabled={location.pathname === "/meet-expert"}
								>
									<svg
										className={styles.menuIcon}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
										/>
									</svg>
									<span>{t("navigation.meetExpert")}</span>
								</button> */}

								{/* Temporarily hidden - will be back later */}
								{/* <button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/pricing" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/pricing", "menu_pricing_click")
									}
									disabled={location.pathname === "/pricing"}
								>
									<CreditCardIcon className={styles.menuIcon} />
									<span>{t("navigation.pricing")}</span>
								</button> */}

								<button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/profile" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/profile", "menu_profile_click")
									}
									disabled={location.pathname === "/profile"}
								>
									<UserIcon className={styles.menuIcon} />
									<span>{t("navigation.profile")}</span>
								</button>

								<button
									type="button"
									className={`${styles.menuItem} ${location.pathname === "/settings" ? styles.active : ""}`}
									onClick={() =>
										handleNavigation("/settings", "menu_settings_click")
									}
									disabled={location.pathname === "/settings"}
								>
									<Cog6ToothIcon className={styles.menuIcon} />
									<span>{t("navigation.settings")}</span>
								</button>

								{isAdmin && (
									<button
										type="button"
										className={`${styles.menuItem} ${location.pathname === "/admin" ? styles.active : ""}`}
										onClick={() =>
											handleNavigation("/admin", "menu_admin_click")
										}
										disabled={location.pathname === "/admin"}
									>
										<ShieldCheckIcon className={styles.menuIcon} />
										<span>{t("navigation.admin")}</span>
									</button>
								)}
							</nav>

							{/* Menu footer with legal links and version */}
							<MenuFooter />
						</>
					)}
				</div>
			</div>
		</>
	);
}
